/**
 * Which dashboard Issues rows just arrived from a bulk create.
 *
 * Membership only; the pacing and the slot arithmetic are shared with the
 * extension sidebar and the team-review drawer (`@overlayqa/shared/cascade`).
 *
 * WHY AN ARM AND NOT A BARE DIFF. `AddScanIssuesModal`'s response carries
 * counts (`added`, `existing`, `capped`) and no ids, so the only way to name
 * the new rows is to diff the list the page already holds. But the Issues table
 * re-queries on a filter change, a status change, a page change and a poll, and
 * any of those can add rows nobody just created. So the diff is believed only
 * inside the window a create opens, and only once.
 *
 * WHY A MODULE AND NOT STATE. The create lives in the modal and the list lives
 * in the page; the modal unmounts on success. A module-level latch is the one
 * place both can reach without threading a callback through the modal's whole
 * close path.
 */
import { newIssueIds } from '@overlayqa/shared';

/**
 * How long a create waits for its rows to show up. Generous because the modal
 * invalidates several query keys and the refetch is a network round-trip;
 * short enough that a create whose refetch never landed cannot colour a filter
 * change minutes later.
 */
export const ARRIVAL_ARM_TTL_MS = 10_000;

let armedAt: number | null = null;

/** Called by the create once the server has accepted the new issues. */
export function armIssueArrivals(now: number = Date.now()): void {
  armedAt = now;
}

/** Test seam, and the reset a page gets when it stops caring. */
export function disarmIssueArrivals(): void {
  armedAt = null;
}

/**
 * The rows `next` added over `previous`, but only while a create is waiting for
 * them.
 *
 * PURE, and that is load-bearing: the dashboard renders under StrictMode
 * (`main.tsx`), which double-invokes every `useMemo`. An earlier draft disarmed
 * the latch here, so the second invocation answered with nothing and the
 * feature was dead in exactly the environment the browser pass runs in. The
 * caller disarms from an effect once it has the answer.
 */
export function peekIssueArrivals(
  previous: readonly string[],
  next: readonly string[],
  now: number = Date.now(),
  loaded: boolean = true,
): string[] {
  if (armedAt === null) return [];
  if (now - armedAt > ARRIVAL_ARM_TTL_MS) return [];
  // A page that has not shown a list yet is LOADING, not receiving. This is
  // not a theoretical guard: a create that lands in another project navigates
  // (`AddScanIssuesModal.tsx:284`), which remounts IssuesPage with an empty
  // previous list while the latch is still armed, and without this every row
  // on the arriving page would cascade.
  if (!loaded) return [];
  return newIssueIds(previous, next);
}
